'use client';
import { GradualSpacing } from '@/components/common/inputs/text/gradual-spacing';
import { CDN_BASE_URL } from '@/lib/constants/cdn';
import { useI18n } from '@/locales/client';
import { motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';

export const WelcomeMessage = React.memo(() => {
  const t = useI18n();
  return (
    <div className='flex flex-col items-center gap-6'>
      {/* Welcome text */}
      <div>
        <GradualSpacing
          className='font-display text-center text-lg font-bold leading-relaxed -tracking-widest text-gray-700 dark:text-gray-100 sm:text-xl md:text-2xl lg:text-3xl'
          text={t('welcome')}
        />
      </div>

      {/* Logo with dynamic sizing */}
      <motion.div
        className='relative aspect-[2/1] max-h-[120px] w-full'
        initial={{ rotate: -360 * 2, opacity: 0 }}
        animate={{ rotate: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 80, damping: 20 }}
      >
        <Image
          fill
          sizes='(max-width: 640px) 100px, (max-width: 768px) 125px, 155px'
          style={{ objectFit: 'contain' }}
          src={`${CDN_BASE_URL}/JAQLNLogo.png`}
          priority
          alt="JAQLYN's Logo"
          className='dark:brightness-[0.9] dark:contrast-125'
        />
      </motion.div>

      {/* Hidden experts link */}
      <h1 className='flex-shrink-0 text-base font-bold text-gray-700 dark:text-gray-100 sm:text-lg md:text-xl'>
        <Link
          href='/advisors'
          prefetch={true}
          className='hidden h-8 items-center space-x-1 rounded bg-gradient-to-l from-purple-400 to-purple-600 p-1 text-xs font-medium text-white transition-colors hover:from-purple-500 hover:to-purple-700 dark:from-purple-500 dark:to-purple-700 dark:hover:from-purple-600 dark:hover:to-purple-800 sm:h-9 sm:space-x-2 sm:p-2 sm:text-sm md:h-10'
        >
          <span>{legalExpertIcon}</span>
          <span className='hidden sm:inline'>{t('experts.title')}</span>
        </Link>
      </h1>
    </div>
  );
});

WelcomeMessage.displayName = 'WelcomeMessage';

const legalExpertIcon = (
  <svg
    width='24'
    height='25'
    viewBox='0 0 24 25'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    className='h-4 w-4 sm:h-5 sm:w-5'
  >
    <path
      d='M21.0799 9.08003V15.92C21.0799 17.04 20.4799 18.08 19.5099 18.65L13.5699 22.08C12.5999 22.64 11.3999 22.64 10.4199 22.08L4.47991 18.65C3.50991 18.09 2.90991 17.05 2.90991 15.92V9.08003C2.90991 7.96003 3.50991 6.91999 4.47991 6.34999L10.4199 2.92C11.3899 2.36 12.5899 2.36 13.5699 2.92L19.5099 6.34999C20.4799 6.91999 21.0799 7.95003 21.0799 9.08003Z'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M11.9999 11.5C13.2867 11.5 14.3299 10.4568 14.3299 9.16998C14.3299 7.88316 13.2867 6.84003 11.9999 6.84003C10.7131 6.84003 9.66992 7.88316 9.66992 9.16998C9.66992 10.4568 10.7131 11.5 11.9999 11.5Z'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M16 17.1595C16 15.3595 14.21 13.8996 12 13.8996C9.79 13.8996 8 15.3595 8 17.1595'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);
